/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "@mui/material/Table";
import DataTable from "../../examples/Tables/DataTable";
import data from "../dashboard/components/Projects/data";
import useApi from "../../components/Utils/api";
import Icon from "@mui/material/Icon";
import ConfiguratorRoot from "../../examples/Configurator/ConfiguratorRoot";
import Divider from "@mui/material/Divider";
import UpdateUser from "./updateUser";
import NewUser from "./newUser";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import PermissionControl from "components/Utils/PermissionControl";

function Users() {
  const {t} = useTranslation();
  const { userTablecolumns } = data();
  const [users, setUsers] = useState([]);
  const [openConfigurator, setOpenConfigurator] = useState(false);
  const [openNewUserConfigurator, setOpenNewUserConfigurator] = useState(false);
  const [updateUser, setUpdateUser] = useState({});
  const [disableUser, setDisableUser] = useState({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const api = useApi();

  const updateUserHandler = (user) => {
    setUpdateUser(user);
    setOpenConfigurator(true);
  };

  const handleDisableItem = (user) => {
    const isCurrentlyActive = user.status === "1" || user.status === 1;
    const action = isCurrentlyActive ? "disabled" : "enabled";
    const icon = isCurrentlyActive ? '🔒' : '✅';
    
    api.post(`/users/status/${user.userId}/toggle`)
    .then((response) => {
      // console.log(response);
      if(response.data.statusCodeValue === 200) {
        toast.success(`User ${action} successfully!`, {
          icon: icon,
          duration: 3000,
        });
        setOpenConfirmDialog(false);
        getUsers();
      }
    })
    .catch((error) => {
      toast.error(`Failed to ${isCurrentlyActive ? 'disable' : 'enable'} user`, { duration: 3000 });
      console.error(error);
    });
  };

  const handleConfirmDisable = (user) => {
    setDisableUser(user);
    setOpenConfirmDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenConfirmDialog(false);
    setDisableUser({});
  };
  const getUsers = () => {
    api.get("users/browse")
      .then((response) => {
        if(response.data.statusCodeValue === 200) {
          // console.log(response.data);
          const modifiedData = response.data.body.map(item => {
            const isActive = item.status === "1" || item.status === 1;
            const action = (
              <MDBox display="flex" alignItems="center" gap={1}>
                <PermissionControl permission={{ restrict: true, module: "User", subModule: "ToggleAdminUserStatus" }} >
                <MDButton
                  variant="contained"
                  size="small"
                  color={isActive ? "error" : "success"}
                  onClick={() => handleConfirmDisable(item)}
                >
                  {isActive ? "Disable" : "Enable"}
                </MDButton>
                </PermissionControl>
                <MDButton
                  variant="contained"
                  size="small"
                  color="info"
                  onClick={() => updateUserHandler(item)}
                >
                  Edit
                </MDButton>
              </MDBox>
            );
            const statusChip = (
              <Chip
                label={isActive ? "Active" : "Inactive"}
                variant="outlined"
                color={isActive ? "success" : "error"}
              />
            );
            if(item.role==='MAKER'){
              item.role = 'ADMIN';
            }
            return { ...item, action, status: statusChip };
          });
          setUsers(modifiedData);
        } else {
          toast.error(response.data.statusCode);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.", { id: "PermissionError" });
          return;
        }
        console.log(error.response);
      })
  }

  useEffect(() => {
    if(openConfigurator === false && openNewUserConfigurator === false) {
      getUsers();
    }
  },[openConfigurator, openNewUserConfigurator]);

  useEffect(() => {
      getUsers();
  },[]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <MDBox mb={2}>
              <MDButton color="info" size="medium" variant="contained" onClick={() => setOpenNewUserConfigurator(true)}>New User</MDButton>
            </MDBox>
            <DataTable
              table={{ columns : userTablecolumns, rows : users }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              canSearch={true}
            />
          </Grid>
        </Grid>
      </MDBox>
      <UpdateUser user={updateUser} openConfigurator={openConfigurator} setOpenConfigurator={setOpenConfigurator} />
      <NewUser openConfigurator={openNewUserConfigurator} setOpenConfigurator={setOpenNewUserConfigurator} />
      
      <Dialog open={openConfirmDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1">
            Do you want to {disableUser.status === "1" || disableUser.status === 1 ? "disable" : "enable"} this user?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={handleCloseDialog}>
            Cancel
          </MDButton>
          <MDButton
            color={disableUser.status === "1" || disableUser.status === 1 ? "error" : "success"}
            variant="contained"
            onClick={() => handleDisableItem(disableUser)}
          >
            {disableUser.status === "1" || disableUser.status === 1 ? "Disable" : "Enable"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Users;
